import { Edit16Regular, Add16Regular, Search16Regular } from '@fluentui/react-icons';
import { Button } from '../ui/Button';
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/Dialog';
import type { Layer, LayerRequest } from './types';
import { FormikProvider, useFormik } from 'formik';
import { InputFormField } from '../formFields/InputFormField';
import { CheckboxFormField } from '../formFields/CheckboxFormField';
import { SelectFormField } from '../formFields/SelectFormField';
import { toast } from 'sonner';
import { getApiErrorMessage } from '@/utils/apiError';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { useState } from 'react';
import { useLayerOperations } from './LayerOperationsContext';
import SolutionPickerModal from '../modals/SolutionPickerModal';

const getDefaultLayer = (): Layer => ({
  id: null,
  order: 0,
  function: 'structure',
  thickness: 0,
  is_core_layer: false,
  is_wrap: false,
  is_structural: false,
  is_variable: false,
  solution: null,
});

type CompositionLayerDialogProps = {
  layer: Layer | null;
  variant: 'add' | 'edit';
  layers: Layer[];
};

const CompositionLayerDialog = ({ layer, variant, layers }: CompositionLayerDialogProps) => {
  const [open, setOpen] = useState(false);
  const [visibleSolutionPickerModal, setVisibleSolutionPickerModal] = useState(false);

  const {
    entityId,
    createLayer,
    updateLayer,
    isCreating,
    isUpdating,
    layerFunctions,
    isLoadingFunctions,
    onSuccess,
  } = useLayerOperations();

  const isLoading = isCreating || isUpdating;

  const form = useFormik<Layer>({
    initialValues: layer || getDefaultLayer(),
    enableReinitialize: true,
    onSubmit: (values) => {
      const requestData: LayerRequest = {
        function: values.function,
        thickness: values.function === 'membrane_layer' ? 0 : values.thickness,
        is_core_layer: values.is_core_layer,
        is_wrap: values.is_wrap,
        is_structural: values.is_structural,
        is_variable: values.is_variable,
        id_classified: values.solution?.id_classified || values.solution?.id || null,
        order: values.order || layers?.length + 1,
      };

      if (variant === 'add') {
        createLayer(
          { instanceTypeId: entityId, data: requestData },
          {
            onSuccess: () => {
              toast.success('Vrstva byla vytvořena');
              setOpen(false);
              form.resetForm();
              onSuccess?.();
            },
            onError: (error) => {
              console.error(error);
              toast.error(getApiErrorMessage(error, 'Vrstvu se nepodařilo vytvořit'));
            },
          }
        );
      } else {
        updateLayer(
          { instanceTypeId: entityId, layerId: values.id, data: requestData },
          {
            onSuccess: () => {
              toast.success('Vrstva byla aktualizována');
              setOpen(false);
              onSuccess?.();
            },
            onError: (error) => {
              console.error(error);
              toast.error(getApiErrorMessage(error, 'Vrstvu se nepodařilo aktualizovat'));
            },
          }
        );
      }
    },
  });

  const isMembraneLayer = form.values.function === 'membrane_layer';

  const handleSubmitSolutionPickerModal = (values: any) => {
    form.setFieldValue('solution', values);
    setVisibleSolutionPickerModal(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button
            data-testid={`composition-layer-dialog-trigger-${variant}`}
            variant="secondary"
            className="size-7"
            size="icon"
          >
            {variant === 'add' ? <Add16Regular /> : <Edit16Regular />}
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-[525px]">
          <DialogHeader>
            <DialogTitle>{variant === 'add' ? 'Přidání vrstvy' : 'Editace vrstvy'}</DialogTitle>
          </DialogHeader>

          <DialogBody>
            <FormikProvider value={form}>
              <form onSubmit={form.handleSubmit} className="space-y-1">
                <SelectFormField
                  name="function"
                  label="Funkce"
                  placeholder="Vyberte funkci"
                  loading={isLoadingFunctions}
                  data={layerFunctions || []}
                />

                <div>
                  <label htmlFor="solution" className="text-sm font-medium text-neutral-700">
                    Řešení
                  </label>

                  <Button
                    name="solution"
                    variant="outline"
                    type="button"
                    data-testid="composition-layer-solution-picker-btn"
                    className="w-full h-10"
                    onClick={() => setVisibleSolutionPickerModal(true)}
                  >
                    {form.values.solution?.title ? null : <Search16Regular />}
                    {form.values.solution?.title || 'Vyberte řešení'}
                  </Button>
                </div>

                <InputFormField
                  disabled={isMembraneLayer}
                  type="number"
                  name="thickness"
                  label="Tloušťka (mm)"
                />

                <div className="grid gap-3 pt-2">
                  <CheckboxFormField
                    disabled={isMembraneLayer}
                    name="is_core_layer"
                    label="Jádrová vrstva"
                  />
                  <CheckboxFormField disabled={isMembraneLayer} name="is_wrap" label="Zalomení" />
                  <CheckboxFormField
                    disabled={isMembraneLayer}
                    name="is_structural"
                    label="Konstrukční"
                  />
                  <CheckboxFormField
                    disabled={isMembraneLayer}
                    name="is_variable"
                    label="Proměnná"
                  />
                </div>
              </form>
            </FormikProvider>
          </DialogBody>

          <DialogFooter>
            <DialogClose asChild>
              <Button
                data-testid="composition-layer-dialog-cancel-btn"
                size="lg"
                variant="outline"
                disabled={isLoading}
              >
                Zrušit
              </Button>
            </DialogClose>
            <Button
              data-testid="composition-layer-dialog-submit-btn"
              size="lg"
              onClick={() => form.handleSubmit()}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <SpinnerOverlay size="sm" />
                  <span>Ukládání...</span>
                </>
              ) : variant === 'add' ? (
                'Vytvořit'
              ) : (
                'Uložit změny'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <SolutionPickerModal
        visible={visibleSolutionPickerModal}
        onClose={() => setVisibleSolutionPickerModal(false)}
        onSubmit={handleSubmitSolutionPickerModal}
        classificationId={null}
        isMaterial
      />
    </>
  );
};

export default CompositionLayerDialog;
